import {
  TServerErrorResponse,
  Conversation,
  GetTableListParams,
  Participant,
  ConversationPurposeEntity,
  ConversationDataTableRowEntity,
  ExcelAiFillResponse,
  SuggestedParticipants,
} from "@/types";
import { http } from "../httpHandler";
import { DATE_FORMAT, links } from "@/constants";
import { format } from "date-fns/format";

export const CONVERSATION_GET = "conversation-get";
export const CONVERSATION_ALL_GET = "conversation-all-get";
export const CONVERSATION_CLIENT_GET = "conversation-client-get";
export const CONVERSATION_EMAIL_FOLLOWUP = "conversation-email-followup";
export const CONVERSATION_DATA_TABLE_ROW_UPDATE =
  "conversation-data-table-row-update";
export const CONVERSATION_DATA_TABLE_ROWS_GET =
  "conversation-data-table-rows-get";
export const SUGGESTED_PARTICIPANTS_GET = "suggested-participants-get";

export interface GetConversationListParams {
  take: number;
  skip: number;
  search: string;
  asc: boolean;
}

export const getConversation = async (
  conversationId?: string,
  params?: { includeDataTableRows: boolean },
) => {
  if (!conversationId) return;
  try {
    const response = await http.get<Conversation>(
      links.conversation.conversationById(conversationId),
      {
        params: {
          includeTranscript: false,
          ...(params || {}),
        },
      },
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const getAllConversations = async (
  params: Partial<GetTableListParams> & {
    userId?: string;
    clientId?: string;
    includeParticipants?: boolean;
    advisors?: string[];
    includeTranscript?: boolean;
  },
) => {
  try {
    const response = await http.get<Conversation[]>(links.conversation.index, {
      params,
      paramsSerializer: {
        indexes: null, // This prevents the [] notation on advisors
      },
    });

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const createConversation = async (note: Conversation) => {
  try {
    const response = await http.post<Conversation>(
      links.conversation.index,
      note,
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const updateConversation = async (
  conversation: Partial<Conversation>,
) => {
  const { id, ...data } = conversation;
  const dataToSend = {
    ...data,
    title:
      data.title ||
      `Meeting on ${format(data.recordedAt ?? new Date(), DATE_FORMAT)}`,
  };
  try {
    const response = await http.patch<Conversation>(
      links.conversation.conversationById(id!),
      dataToSend,
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const createParticipant = async (
  participant: Partial<Participant> & { author?: string },
) => {
  try {
    if (!participant?.conversationId) return;
    const { author, ...rest } = participant;
    const response = await http.post<Participant>(
      links.participant.index,
      rest,
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const updateParticipant = async (participant: Partial<Participant>) => {
  try {
    if (!participant?.id || !participant.conversationId) return;
    const { id, conversationId, ...rest } = participant;
    const response = await http.patch<Participant>(
      links.participant.participantById(id),
      rest,
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const deleteConversation = async (conversationId: string) => {
  try {
    const response = await http.delete<Conversation>(
      links.conversation.conversationById(conversationId),
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const deleteParticipant = async (participantId: string) => {
  try {
    const response = await http.delete<Participant>(
      links.participant.participantById(participantId),
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export interface ConversationEmailFollowup {
  subject: string;
  body: string;
  /**
   * Id of the template used at generation time. May reference a since-deleted
   * template (no FK on the backend) — don't assume it resolves against the
   * user's template list. Null when the email was generated with the built-in
   * default (no template row) or predates template tracking.
   */
  templateId: string | null;
  /**
   * Denormalized snapshot of the template's name at generation time —
   * survives template rename/deletion. Null whenever templateId is null.
   */
  templateName: string | null;
}

export const getConversationEmailFollowUp = async (conversationId?: string) => {
  if (!conversationId) return;
  try {
    const response = await http.get<ConversationEmailFollowup>(
      links.conversation.emailFollowUpByConversationId(conversationId),
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

// Omitting both resolves the user's default template, falling back to the
// Standard email; `useStandardTemplate` asks for the Standard email outright.
export const postGenerateEmailFollowup = async ({
  conversationId,
  templateId,
  useStandardTemplate,
}: {
  conversationId?: string;
  templateId?: string;
  useStandardTemplate?: boolean;
}) => {
  if (!conversationId) return;
  try {
    const response = await http.post<ConversationEmailFollowup>(
      links.conversation.emailFollowUpByConversationId(conversationId),
      { templateId, useStandardTemplate },
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const patchConversationEmailFollowUp = async ({
  conversationId,
  subject,
  body,
}: {
  conversationId?: string;
  subject?: string;
  body?: string;
}) => {
  if (!conversationId) return;
  try {
    // PATCH returns only the editable fields — never templateId/templateName.
    // Cache consumers must merge, not replace (see useEmailHandoff).
    const response = await http.patch<
      Pick<ConversationEmailFollowup, "subject" | "body">
    >(links.conversation.emailFollowUpByConversationId(conversationId), {
      subject,
      body,
    });

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export enum PdfExportType {
  TRANSCRIPT = "transcript",
  SUMMARY = "summary",
  COMBINED = "combined",
}

export interface ConversationPdfExportRequestPayload {
  exportType?: PdfExportType;
  title?: string;
  includeTimestamps?: boolean;
  fontSize?: number;
  lineHeight?: number;
}

export const postCreateTranscriptPDF = async (
  conversationId: string,
  payload?: ConversationPdfExportRequestPayload,
) => {
  if (!conversationId) return;
  try {
    const response = await http.post(
      links.conversation.createPDF(conversationId),
      payload || {},
      {
        responseType: "blob",
        headers: { accept: "application/pdf" },
      },
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const putReprocessConversation = async ({
  conversationId,
  purpose,
  customPrompt,
}: {
  conversationId?: string;
  purpose?: ConversationPurposeEntity | null;
  customPrompt?: string | null;
}) => {
  if (!conversationId) return;
  const data: {
    purpose?: ConversationPurposeEntity | null;
    customPrompt?: string | null;
  } = {};
  if (purpose !== undefined) {
    data.purpose = purpose;
    if (purpose !== null) data.customPrompt = null;
  }
  if (customPrompt !== undefined) data.customPrompt = customPrompt;
  try {
    const response = await http.put<Conversation>(
      links.conversation.reprocess(conversationId),
      data,
    );

    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const updateConversationDataTableRow = async (
  rowId: string,
  payload: Partial<ConversationDataTableRowEntity>,
): Promise<ConversationDataTableRowEntity> => {
  try {
    const response = await http.patch<ConversationDataTableRowEntity>(
      links.conversationDataTableRow.byId(rowId),
      payload,
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export interface ConversationTemplatePrefillResponse {
  filledData: Record<string, string>;
  templateId: string;
  templateName: string;
  fieldsFilled: number;
  fieldsTotal: number;
}

export const fillConversationPdfTemplate = async (
  conversationId: string,
  templateId: string,
): Promise<ConversationTemplatePrefillResponse> => {
  try {
    const response = await http.get<ConversationTemplatePrefillResponse>(
      links.conversation.prefillPdfTemplate(conversationId, templateId),
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const fillConversationExcelTemplate = async (
  conversationId: string,
  templateId: string,
): Promise<ExcelAiFillResponse> => {
  try {
    const response = await http.get<ExcelAiFillResponse>(
      links.conversation.prefillExcelTemplate(conversationId, templateId),
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const getConversationDataTableRows = async (
  conversationId: string,
): Promise<ConversationDataTableRowEntity[]> => {
  if (!conversationId) return [];
  try {
    const response = await http.get<ConversationDataTableRowEntity[]>(
      links.conversationDataTableRow.byConversationId(conversationId),
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};

export const getSuggestedParticipants = async (
  conversationId?: string,
): Promise<SuggestedParticipants> => {
  if (!conversationId) return [];
  try {
    const response = await http.get<SuggestedParticipants>(
      links.conversation.suggestedParticipants(conversationId),
    );
    return response.data;
  } catch (error) {
    throw error as TServerErrorResponse;
  }
};
